/**
 * Provider Service
 * Handles provider configuration operations for white-label widget
 */

import { apiClient, ApiClient } from './api';
import type {
  AdditionalService,
  ProhibitedItemsCategory,
  ServiceAgreement,
} from '@/types/provider.types';

// Re-export shared types for backwards compatibility
export type {
  AdditionalService,
  ProhibitedItemsCategory,
  ServiceAgreement,
  ServiceCategory,
  CreateAdditionalServiceRequest,
  UpdateAdditionalServiceRequest,
} from '@/types/provider.types';

export interface ServiceOffering {
  containerSize: string;
  description: string;
  available: boolean;
}

export interface PricingConfig {
  basePricing: Record<string, number>;
  deliveryFee?: number;
  disposalFees?: Record<string, number>;
  rentalPeriodDays: number;
  overageFeePerDay?: number;
  environmentalFee?: number;
  taxRate?: number;
}

export interface EnhancedPricingDisplay {
  // What's included section
  includedTonnage: Record<string, number>; // { "20Y": 2, "30Y": 3, "40Y": 5 }
  rentalPeriodDays: number;
  freeDays: number;

  // Additional charges section
  overageRatePerTon: number;
  dailyExtensionRate: number;

  // Material restrictions section
  contaminationFeeRange?: {
    min: number;
    max: number;
  };
}

export interface DeliveryTimeSettings {
  type: 'same_day' | 'next_day' | 'custom';
  sameDayCutoffHour?: number; // 24-hour format, e.g., 14 for 2pm
  sameDayDeliveryHour?: number; // 24-hour format, e.g., 17 for 5pm
  nextDayDeliveryHour?: number; // 24-hour format, e.g., 12 for noon
  customEstimateHours?: number; // Hours from order time
  enabled: boolean;
}

export interface SocialProofSettings {
  enabled: boolean;
  showPopularityBadges: boolean;
  showRegionalActivity: boolean;
  showProviderTrust: boolean;
  minimumOrdersThreshold: number;
  minimumPercentageThreshold: number;
}

export interface ProviderConfig {
  companyName: string;
  logoUrl?: string;
  primaryColor: string;
  contactEmail: string;
  contactPhone: string;
  website?: string;
  services: ServiceOffering[];
  pricing: PricingConfig;
  enhancedPricing?: EnhancedPricingDisplay;
  encoreEnabled: boolean;
  deliveryTimeSettings?: DeliveryTimeSettings;
  socialProofSettings?: SocialProofSettings;
  additionalServices?: AdditionalService[];
  prohibitedItems?: ProhibitedItemsCategory[];
  serviceAgreement?: ServiceAgreement;
}

/**
 * ProviderService class
 *
 * Provides methods to fetch provider configuration for white-label theming.
 * Public endpoint - no authentication required.
 */
export class ProviderService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * Get provider configuration by slug
   *
   * @param providerSlug - Provider slug (e.g., 'paso-rolloff')
   * @returns Promise<ProviderConfig> - Provider configuration
   */
  async getConfig(providerSlug: string): Promise<ProviderConfig> {
    const response = await this.client.get<ProviderConfig>(
      `/widget/${providerSlug}/config`
    );

    return response;
  }

  /**
   * Validate service area for a given address
   * Uses geospatial validation when full address provided, falls back to ZIP-only validation
   *
   * @param providerSlug - Provider slug
   * @param address - Address components
   * @returns Promise with validation result and jurisdiction info
   */
  async validateServiceArea(
    providerSlug: string,
    address: {
      street?: string;
      city?: string;
      state?: string;
      zipCode: string;
    }
  ): Promise<{
    inServiceArea: boolean;
    message?: string;
    jurisdiction?: {
      id: string;
      name: string;
      type: string;
    };
    pricingUnavailable?: boolean;
    pricing?: {
      type: 'fixed' | 'dynamic';
      fixedRates?: {
        '10Y': number;
        '20Y': number;
        '30Y': number;
        '40Y': number;
      };
      dynamicQuote?: {
        estimatedPriceCents: number;
        breakdown: {
          baseCostCents: number;
          distanceCostCents: number;
          timeCostCents: number;
          markupPercent: number;
        };
        estimatedDriveMinutes: number;
        estimatedDistanceMiles: number;
      };
    };
  }> {
    return this.client.post(`/widget/${providerSlug}/service-area/validate`, address);
  }

  /**
   * Get availability for a date range
   *
   * @param providerSlug - Provider slug
   * @param start - Start date in YYYY-MM-DD format
   * @param end - End date in YYYY-MM-DD format
   * @returns Promise with availability data
   */
  async getAvailability(providerSlug: string, start: string, end: string): Promise<{
    availability: Record<string, { status: string; containers: number }>;
  }> {
    return this.client.get(`/widget/${providerSlug}/availability?start=${start}&end=${end}`);
  }

  /**
   * Calculate pricing for container and material type
   *
   * @param providerSlug - Provider slug
   * @param data - Pricing calculation data
   * @returns Promise with pricing information
   */
  async calculatePricing(providerSlug: string, data: {
    containerSize: string;
    materialType: string;
    zipCode: string;
  }): Promise<{
    rentalFee: number;
    displayPrice: string;
  }> {
    return this.client.post(`/widget/${providerSlug}/pricing/calculate`, data);
  }

  /**
   * Estimate pricing for container order with detailed breakdown
   *
   * @param providerSlug - Provider slug
   * @param params - Pricing estimation parameters
   * @returns Promise with detailed pricing estimate including fees and warnings
   */
  async estimatePricing(
    providerSlug: string,
    params: {
      containerSize: string;
      materialType: string;
      selectedServices?: Array<{ serviceId: string; quantity: number }>;
      estimatedTonnage?: number;
      hasTreatedWood?: boolean;
      estimatedWeightTons?: number;
      serviceAreaType?: string;
      pricingModelId?: string;
    }
  ): Promise<{
    basePrice: number;
    includedTonnage?: number;
    overageFee?: number;
    overweightFee?: number;
    serviceFees?: Array<{
      serviceId: string;
      serviceName: string;
      quantity: number;
      amount: number;
    }>;
    totalEstimate: number;
    warnings: Array<{
      type: 'CONTAMINATION' | 'OVERWEIGHT' | 'SPECIALTY_VIOLATION' | 'INFO';
      severity: 'INFO' | 'WARNING' | 'ERROR';
      message: string;
      estimatedCostImpact?: number;
    }>;
    requiredAcknowledgments: string[];
    pricingModelUsed: string;
    notes?: string[];
  }> {
    return this.client.post(`/widget/${providerSlug}/pricing/estimate`, params);
  }
}

// Export singleton instance
export const providerService = new ProviderService();
